import React, { useEffect, useState } from 'react';
import { Card, Table, Typography, Space, Button, Tag, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { api } from '../services/api';
import { useCurrency } from '../contexts/CurrencyContext';

const { Title, Text } = Typography;

interface ProductMovementItem {
  id: number;
  movementDate: string;
  movementLabel: string;
  quantity: number;
  quantityAbs: number;
  balanceQuantity: number;
  warehouseName?: string | null;
  variantCode?: string | null;
  documentNo?: string | null;
  serviceOrderNo?: string | null;
  serviceCustomerName?: string | null;
  unitPrice?: number | null;
  totalPrice?: number | null;
}

interface ProductMovementResponse {
  productId: number;
  productCode: string;
  productName: string;
  taxRate: number;
  summary: {
    lastPurchasePrice?: number | null;
    lastSalePrice?: number | null;
    systemSalePrice?: number | null;
    averagePurchasePrice?: number | null;
  };
  items: ProductMovementItem[];
}

const ProductMovements: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { baseCurrencySymbol } = useCurrency();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ProductMovementResponse | null>(null);

  const productId = Number(id);

  const loadData = async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const { data: response } = await api.get<ProductMovementResponse>(`/products/${productId}/movements`);
      setData(response);
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Ürün hareketleri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const formatMoney = (v?: number | null) =>
    v == null ? '-' : `${baseCurrencySymbol}${v.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const columns = [
    {
      title: 'Tarih',
      dataIndex: 'movementDate',
      key: 'movementDate',
      width: 150,
      render: (v: string) => dayjs(v).format('DD.MM.YYYY HH:mm')
    },
    {
      title: 'Tür',
      dataIndex: 'movementLabel',
      key: 'movementLabel',
      width: 110,
      render: (v: string) => {
        const color = v === 'Alış' ? 'green' : v === 'Satış' ? 'blue' : 'default';
        return <Tag color={color}>{v}</Tag>;
      }
    },
    {
      title: 'Belge / Servis',
      key: 'source',
      width: 220,
      render: (_: unknown, r: ProductMovementItem) => {
        if (r.serviceOrderNo) return `${r.serviceOrderNo}${r.serviceCustomerName ? ` - ${r.serviceCustomerName}` : ''}`;
        return r.documentNo || '-';
      }
    },
    { title: 'Varyant', dataIndex: 'variantCode', key: 'variantCode', width: 120, render: (v: string | null) => v || '-' },
    { title: 'Depo', dataIndex: 'warehouseName', key: 'warehouseName', width: 130, render: (v: string | null) => v || '-' },
    { title: 'Miktar', dataIndex: 'quantity', key: 'quantity', width: 90, align: 'right' as const },
    { title: 'Birim Fiyat', dataIndex: 'unitPrice', key: 'unitPrice', width: 120, align: 'right' as const, render: (v: number | null) => formatMoney(v) },
    { title: 'Toplam', dataIndex: 'totalPrice', key: 'totalPrice', width: 120, align: 'right' as const, render: (v: number | null) => formatMoney(v) },
    { title: 'Bakiye', dataIndex: 'balanceQuantity', key: 'balanceQuantity', width: 90, align: 'right' as const }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginBottom: 12 }} wrap>
          <div>
            <Title level={4} style={{ margin: 0 }}>
              Ürün Hareketleri
            </Title>
            <Text type="secondary">
              {data ? `${data.productCode} - ${data.productName}` : ''}
            </Text>
          </div>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/products')}>
            Ürünlere Dön
          </Button>
        </Space>

        <Space style={{ marginBottom: 16 }} wrap>
          <Card size="small" title="Son Alış Fiyatı">{formatMoney(data?.summary?.lastPurchasePrice)}</Card>
          <Card size="small" title="Son Satış Fiyatı">{formatMoney(data?.summary?.lastSalePrice)}</Card>
          <Card size="small" title="Sistemdeki Satış Fiyatı">{formatMoney(data?.summary?.systemSalePrice)}</Card>
          <Card size="small" title="Ortalama Fiyat (Paçal)">{formatMoney(data?.summary?.averagePurchasePrice)}</Card>
        </Space>

        <Table
          rowKey="id"
          loading={loading}
          dataSource={data?.items ?? []}
          columns={columns}
          scroll={{ x: 1200 }}
          pagination={{ pageSize: 20, showTotal: t => `Toplam ${t} hareket` }}
        />
      </Card>
    </div>
  );
};

export default ProductMovements;
